import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { BrowserQRCodeReader } from "@zxing/library" // You can also use BrowserMultiFormatReader if needed

const SCAN_THROTTLE_MS = 2000

export default class extends Controller {
  static targets = ["selectedDevice", "result", "modal"]

  connect() {
    this.initCameraOptions()
    this._modalHiddenHandler = this.stopCamera.bind(this)
    this.modalTarget.addEventListener("hidden.bs.modal", this._modalHiddenHandler)
  }

  disconnect() {
    if (this._modalHiddenHandler) {
      this.modalTarget.removeEventListener("hidden.bs.modal", this._modalHiddenHandler)
    }
    this.stopCamera()
  }
  async initCameraOptions() {
    const codeReader = new BrowserQRCodeReader()
    const sourceSelect = this.selectedDeviceTarget
    sourceSelect.innerHTML = "" // Clear options

    try {
      const devices = await codeReader.listVideoInputDevices()
      devices.forEach(({ label, deviceId }, index) => {
        sourceSelect.add(new Option(label, deviceId, index === 0, index === 0)) // Add and select the first device
      })
    } catch (error) {
      console.error("Error listing video input devices:", error)
    }
  }

  async start() {
    const workspaceId = document.body.getAttribute("data-params-workspace-id")

    if (!workspaceId || workspaceId.trim() === "") {
      console.error("QR Code Reader: Workspace ID not found. Cannot process QR codes.")
      if (this.hasResultTarget) {
        this.resultTarget.textContent = "Unable to scan: missing workspace context."
      }
      return
    }

    try {
      const stream = await this.getCameraPermission()
      this.stream = stream

      const selectedDeviceId = this.selectedDeviceTarget.value

      this.codeReader = new BrowserQRCodeReader()
      this.isProcessing = false

      this.codeReader
        .decodeFromVideoDevice(selectedDeviceId, "video", async (result, error) => {
          if (!result && error) {
            console.debug("QR decode error:", error)
          }

          if (result && !this.isProcessing) {
            this.isProcessing = true
            this.resultTarget.textContent = result.text
            try {
              await this.sendQRCodeData(result.text, workspaceId)
            } finally {
              this.isProcessing = false
            }
          }
        })
        .catch((error) => console.error("Could not access video device", error))
    } catch (error) {
      console.error("Error starting QR code reader:", error)
    }
  }

  async getCameraPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      await this.initCameraOptions()
      return stream
    } catch (error) {
      console.error("Failed to get access to camera:", error)
      throw error
    }
  }

  async sendQRCodeData(qrCodeData, workspaceId) {
    try {
      const response = await post(`/${workspaceId}/expenses/qr_code`, {
        body: JSON.stringify({ expense_id: qrCodeData }),
        contentType: "application/json",
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        },
        responseKind: "turbo"
      })

      if (!response.ok) {
        console.error("QR code request failed with status:", response.statusCode)
      }
    } catch (error) {
      console.error("Error sending QR code data:", error)
    } finally {
      // Throttle to prevent sending multiple requests (runs on success or failure)
      await new Promise((resolve) => setTimeout(resolve, SCAN_THROTTLE_MS))
    }
  }

  stopCamera() {
    if (this.codeReader) {
      this.codeReader.reset() // Stop the QR code reader and stream
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop()) // Stop all video/audio tracks
    }
  }
}
